import { useMemo, useState } from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import {
  FinanceDealsSelectorDocument,
  FinanceValueObjectsDealStageEnum,
} from 'generated/resources/graphql'

import { EntitySelectWrapper } from 'modules/core/components/EntitySelectWrapper'
import LabeledInput from 'modules/core/components/LabeledInput'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { mapToEntitySelectEntity, mapToFinanceDealSelectorFragment } from './mappers'
import { FinanceDealsSelectorProps, GroupType } from './types'

export const FinanceDealsSelector = (props: FinanceDealsSelectorProps) => {
  const {
    disabled,
    value,
    queryParams,
    alwaysOpen,
    defaultOpen,
    width,
    minWidth,
    error,
    mandatory,
    label,
    emptyLabel,
    hideSearchIcon,
    excludeClosedDeals,
    variant = 'f0',
  } = props

  const [skip, setSkip] = useState<boolean>(!(alwaysOpen || defaultOpen))

  const openStages: FinanceValueObjectsDealStageEnum[] | undefined = excludeClosedDeals
    ? ['new', 'qualification', 'proposal_sent', 'negotiation']
    : undefined

  const { loading, data } = useQuery(FinanceDealsSelectorDocument, {
    variables: {
      clientIds: queryParams?.clientIds,
      stages: openStages,
    },
    skip,
  })

  const financeDeals = data?.finance.deals

  const onOpen = () => {
    if (skip) setSkip(false)
  }

  const financeDealsEntities = useMemo(
    () => mapToEntitySelectEntity(financeDeals ?? []),
    [financeDeals]
  )

  const selectedFinanceDeals = useMemo(
    () =>
      value
        ? [
            {
              id: value.id,
              name: value ? value.name : '',
            },
          ]
        : [],
    [value, financeDealsEntities]
  )

  const groupMap: Record<GroupType, EntitySelectEntity[]> = useMemo(
    () => ({
      all: financeDealsEntities,
    }),
    [financeDealsEntities]
  )

  const selectedGroupMap: Record<GroupType, EntitySelectEntity[]> = useMemo(
    () => ({
      all: selectedFinanceDeals ?? [],
    }),
    [selectedFinanceDeals]
  )

  const handleOnSelect = (selection: EntitySelectEntity | null) => {
    if (financeDeals) {
      props.onSelect(selection ? mapToFinanceDealSelectorFragment(selection, financeDeals) : null)
    }
  }

  const firstFinanceDeal = selectedFinanceDeals[0]
  const name = !!value && firstFinanceDeal ? firstFinanceDeal.name : undefined

  return (
    <Box minWidth={minWidth} width={width ?? minWidth ?? 'full'} position="relative">
      <EntitySelectWrapper
        {...props}
        value={props.value}
        label={label ?? i18n.t('finance.deals.selector.select_deals')}
        hideLabel={variant === 'gamma'}
        hiddenAvatar
        loading={loading}
        onOpen={onOpen}
        entities={groupMap['all'] || []}
        selectedGroup={'all'}
        selectedGroupedEntities={selectedGroupMap['all']}
        onSelectExpandedElements={() => {}}
        expandedElements={[]}
        selectedEntities={selectedFinanceDeals}
        searchPlaceholder={i18n.t('finance.deals.selector.search')}
        selectAllLabel={i18n.t('finance.deals.selector.select_all')}
        clearLabel={i18n.t('finance.deals.selector.clear')}
        selectedLabel={i18n.t('finance.deals.selector.selected')}
        notFoundSubtitle={i18n.t('finance.deals.selector.not_found.subtitle')}
        notFoundTitle={i18n.t('finance.deals.selector.not_found.title')}
        placeholder={i18n.t('finance.deals.selector.select_deals')}
        selectedItemsCopy={i18n.t('finance.deals.selector.deals_selected')}
        groups={[{ label: i18n.t('finance.deals.selector.groups.none'), value: 'all' }]}
        onGroupChange={() => {}}
        singleSelector
        onSelect={handleOnSelect}
        onSelectMultiple={() => {}}
        minWidth={minWidth}
        width={width}
        size="md"
      >
        {variant === 'gamma' && (
          <Box flexDirection="row" width="full">
            <LabeledInput
              aria-label="serial-sequence-selector"
              readonly={disabled}
              error={error}
              mandatory={mandatory}
              label={label}
              value={value?.toString() ?? undefined}
            >
              <Box
                paddingX="s16"
                paddingY="s8"
                flexDirection="row"
                height="s40"
                alignItems="center"
                width="full"
                cursor={disabled ? 'default' : 'pointer'}
              >
                <Box flexDirection="row" width="full" gap="s6">
                  {!hideSearchIcon && (
                    <Icon.Medium icon="search" color={disabled ? 'grey600' : 'grey800'} />
                  )}
                  <Box overflowX="hidden" textAlign="left">
                    <Text.Normal ellipsis>
                      {name ?? emptyLabel ?? i18n.t('finance.deals.selector.empty_label')}
                    </Text.Normal>
                  </Box>
                </Box>
                <Icon.Small icon="caret-down" />
              </Box>
            </LabeledInput>
          </Box>
        )}
      </EntitySelectWrapper>
    </Box>
  )
}
